import { formatDistanceToNow } from 'date-fns';

import type { RouterOutputs } from '@poseidon/api/types';
import { cn } from '@poseidon/components/cn';

import { faceTone, nodeNeedsHuman, nodeStatusLabel } from '~/components/Sequence/sequenceSteps';

/**
 * What became of one authored step for the open contact. Aliased straight off the route rather than
 * restated, so the server's closed unions (`state`, `deadReason`, `skipReason`, `outcome`) reach the
 * mapping below intact — a hand-written copy would widen them to `string` and let a renamed reason drift
 * into a silent fallback instead of a type error. The alias still gives the graph components one import
 * point, which is why it lives here rather than in each of them.
 */
export type SequenceContactStepStatus =
  RouterOutputs['sequence']['enrollmentGraphsForContact']['enrollments'][number]['stepStatus'][string];

/**
 * The visual buckets, which must be told apart at a glance on a 36px tile:
 * `done` solid green with a check · `skipped` green struck through with a 45° hatch (it never actually ran
 * for this contact) · `notTaken` gray + hatched (a lane they never entered) · `running` blue ·
 * `needsYou` orange (a to-do or a gate waiting on a person) · `blocked` amber (stuck, auto-retrying) ·
 * `errored` red · `upcoming` the neutral default.
 *
 * Deliberately the same language the touch-campaign graph uses (`touchStepStatus.ts`): green for the happy
 * path, hatch for "didn't actually run", orange for "you owe an action", red for broken. A rep reads one
 * graph having learnt the other.
 */
export type SequenceStepTone =
  | 'upcoming'
  | 'done'
  | 'skipped'
  | 'notTaken'
  | 'running'
  | 'needsYou'
  | 'blocked'
  | 'errored';

/**
 * 45° diagonal hatches — 1.5px lines every 5px — layered as a background *image* over the tone's fill
 * (which shows through the gaps). Same technique as the touch graph's `SKIPPED_HATCH` and `CounterBox`.
 *
 * These MUST stay written out literally: Tailwind generates arbitrary classes by scanning source text, so a
 * class assembled at runtime (`` `bg-[...${color}...]` ``) produces no CSS at all and the hatch silently
 * vanishes. Tune geometry here: `_3.5px` = gap, `_5px` = period (line width = 5 − 3.5 = 1.5px).
 */
const HATCH_GREEN =
  'bg-[repeating-linear-gradient(45deg,transparent,transparent_3.5px,var(--color-mantis-400)_3.5px,var(--color-mantis-400)_5px)] ' +
  'dark:bg-[repeating-linear-gradient(45deg,transparent,transparent_3.5px,var(--color-mantis-500)_3.5px,var(--color-mantis-500)_5px)]';
const HATCH_GRAY =
  'bg-[repeating-linear-gradient(45deg,transparent,transparent_3.5px,var(--color-lynch-300)_3.5px,var(--color-lynch-300)_5px)] ' +
  'dark:bg-[repeating-linear-gradient(45deg,transparent,transparent_3.5px,var(--color-lynch-700)_3.5px,var(--color-lynch-700)_5px)]';
/**
 * The same hatches, a shade lighter, for the WIDE nodes (the wait / await / withdraw pills). A hatch that
 * reads as texture behind a 20px icon reads as noise behind a line of text — these keep the label legible
 * while the node still says "this never ran".
 */
const HATCH_GREEN_SOFT =
  'bg-[repeating-linear-gradient(45deg,transparent,transparent_3.5px,var(--color-mantis-200)_3.5px,var(--color-mantis-200)_5px)] ' +
  'dark:bg-[repeating-linear-gradient(45deg,transparent,transparent_3.5px,var(--color-mantis-800)_3.5px,var(--color-mantis-800)_5px)]';
const HATCH_GRAY_SOFT =
  'bg-[repeating-linear-gradient(45deg,transparent,transparent_3.5px,var(--color-lynch-200)_3.5px,var(--color-lynch-200)_5px)] ' +
  'dark:bg-[repeating-linear-gradient(45deg,transparent,transparent_3.5px,var(--color-lynch-800)_3.5px,var(--color-lynch-800)_5px)]';

/** Border + fill + icon colour for the node itself (icon tile or timing pill). */
const TONE_CHIP: Record<SequenceStepTone, string> = {
  upcoming: '',
  done: 'border-mantis-300 bg-mantis-50 text-mantis-700 dark:border-mantis-500/50 dark:bg-mantis-500/10 dark:text-mantis-300',
  skipped: cn(
    'border-mantis-200 bg-mantis-50 text-mantis-700 dark:border-mantis-500/40 dark:bg-mantis-500/10 dark:text-mantis-300',
    HATCH_GREEN,
  ),
  notTaken: cn(
    'border-lynch-200 bg-lynch-50 text-lynch-400 shadow-none dark:border-slate-700 dark:bg-slate-800/60 dark:text-slate-500',
    HATCH_GRAY,
  ),
  running: 'border-blue-300 bg-blue-50 text-blue-600 dark:border-blue-500/50 dark:bg-blue-500/10 dark:text-blue-300',
  needsYou:
    'border-blaze-orange-300 bg-blaze-orange-50 text-blaze-orange-700 dark:border-blaze-orange-500/50 dark:bg-blaze-orange-500/10 dark:text-blaze-orange-300',
  blocked: 'border-amber-300 bg-amber-50 text-amber-700 dark:border-amber-500/50 dark:bg-amber-500/10 dark:text-amber-300',
  errored:
    'border-fair-pink-300 bg-fair-pink-50 text-fair-pink-600 dark:border-fair-pink-500/50 dark:bg-fair-pink-950/40 dark:text-fair-pink-300',
};

/**
 * The ring drawn on the node's WRAPPER (offset, so it reads as a halo around the whole node rather than a
 * second border) — reserved for "the contact is here right now". Validation rings live on the chip itself,
 * so the two never fight for the same edge.
 */
const TONE_RING: Record<SequenceStepTone, string> = {
  upcoming: '',
  done: '',
  skipped: '',
  notTaken: '',
  running: 'ring-2 ring-blue-400 ring-offset-2 ring-offset-slate-50 dark:ring-offset-neutral-900',
  needsYou: 'ring-2 ring-blaze-orange-400 ring-offset-2 ring-offset-slate-50 dark:ring-offset-neutral-900',
  blocked: 'ring-2 ring-amber-400 ring-offset-2 ring-offset-slate-50 dark:ring-offset-neutral-900',
  errored: 'ring-2 ring-fair-pink-400 ring-offset-2 ring-offset-slate-50 dark:ring-offset-neutral-900',
};

/** Only the hatched tones differ between a tile and a wide pill; everything else shares one class string. */
const TONE_CHIP_WIDE: Partial<Record<SequenceStepTone, string>> = {
  skipped: cn(
    'border-mantis-200 bg-mantis-50 text-mantis-700 dark:border-mantis-500/40 dark:bg-mantis-500/10 dark:text-mantis-300',
    HATCH_GREEN_SOFT,
  ),
  notTaken: cn(
    'border-lynch-200 bg-lynch-50 text-lynch-400 shadow-none dark:border-slate-700 dark:bg-slate-800/60 dark:text-slate-500',
    HATCH_GRAY_SOFT,
  ),
};

/**
 * The wide pills (wait / await / withdraw) hardcode neutral greys on their icon + label, so the tone has to
 * reach in and override them. Applied to the PILLS ONLY: an icon tile's glyph already inherits the tile's
 * own `text-*`, and forcing `currentColor` on its descendants would repaint the corner medallion — whose
 * whole job is a white glyph on a coloured circle — in the tone colour, at 14px, invisible.
 */
const TONE_INHERIT = '[&_svg]:text-current [&_span]:text-current';

const DEAD_REASON_TOOLTIP: Record<string, string> = {
  not_accepted:
    'Never ran — the invite wasn’t accepted in time, so the sequence took the “if not accepted” branch instead.',
  switched: 'Never ran — the contact matched a different branch.',
  switch_no_match: 'Never ran — the contact matched none of the branches, so the sequence ended at the switch.',
  branch_not_taken: 'Never ran — the contact never entered this branch.',
  superseded: 'Never ran — this run was replaced when the contact was restarted.',
  errored: 'Never ran — the sequence stopped on an earlier step. Restart the contact to pick it back up.',
};

/**
 * The badge on a settled node's corner. `check` = it happened; `pending` = it happened but hasn't landed
 * (an invitation still hanging); `withdrawn` = it happened and was taken back. Small enough to read as a
 * status dot at 36px while the tone carries the colour.
 */
export type SequenceStepMedallion = 'check' | 'pending' | 'withdrawn';

export interface SequenceStepVisual {
  tone: SequenceStepTone;
  /** Classes for a small icon tile. */
  chip: string;
  /** Classes for a WIDE node (the wait / await / withdraw pills) — a lighter hatch, so the label stays legible. */
  chipWide: string;
  /** Classes for the wrapper — the "you are here" halo (empty for every settled state). */
  ring: string;
  /** True while the contact is resting on this step (drives the gutter's NOW marker). */
  isHere: boolean;
  /**
   * The sequence is still going here, so a control that advances it (Mark done) can actually do something.
   * False for `errored` — that arm has ENDED, so completing its to-do would no-op while the toast claimed
   * the sequence moved on.
   */
  isLive: boolean;
  /** Short stage read-out for the marker beside a live node, e.g. "Waiting for working hours". */
  stageLabel: string | null;
  /** Hover explanation of what this state means for this contact. */
  tooltip: string;
  /** Corner badge for icon tiles (a pill's fill already reads as settled, so pills don't draw one). */
  medallion: SequenceStepMedallion | null;
}

/** The tone for a step the contact is currently resting on, from the journey node the arm sits on. */
function liveTone(nodeId: string | undefined): SequenceStepTone {
  if (nodeId == null) return 'running';
  const alert = faceTone(nodeId);
  if (alert === 'error') return 'errored';
  if (alert === 'blocked') return 'blocked';
  return nodeNeedsHuman(nodeId) ? 'needsYou' : 'running';
}

/** "3 weeks ago" for a relationship timestamp, or null when the server had no date to give. */
function ago(at: Date | null | undefined): string | null {
  return at == null ? null : formatDistanceToNow(at, { addSuffix: true });
}

/** Copy for a `connect` step whose real-world outcome we can still observe. */
function connectOutcomeTooltip(outcome: string | undefined, at: Date | null | undefined): string | null {
  const when = ago(at);
  if (outcome === 'connected') return when == null ? 'Connected.' : `Connected ${when}.`;
  if (outcome === 'invite_pending')
    return when == null ? 'Invite sent — not accepted yet.' : `Invite sent ${when} — not accepted yet.`;
  if (outcome === 'invite_withdrawn') return when == null ? 'Invite withdrawn.' : `Invite withdrawn ${when}.`;
  return null;
}

const SKIP_REASON_TOOLTIP: Record<string, (when: string | null) => string> = {
  already_connected: (when) =>
    when == null
      ? 'Skipped — you were already connected, so no invite was sent.'
      : `Skipped — you were already connected (${when}), so no invite was sent.`,
};

const TONE_TOOLTIP: Record<SequenceStepTone, string> = {
  upcoming: 'Hasn’t run yet.',
  done: 'Done — this step ran for this contact.',
  skipped: 'Skipped — the sequence moved past this step without running it.',
  notTaken: DEAD_REASON_TOOLTIP.branch_not_taken!,
  running: 'This contact is here right now.',
  needsYou: 'Waiting on you — the sequence can’t continue until this is done.',
  blocked: 'Stuck here, retrying automatically.',
  errored: 'The sequence stopped here.',
};

/**
 * How one step should look for the open contact. `null` (no status — the builder, or a step the server
 * didn't report on) leaves the node in its neutral authoring style, so nothing about the builder changes.
 */
export function sequenceStepVisual(status: SequenceContactStepStatus | null | undefined): SequenceStepVisual | null {
  if (status == null) return null;
  const tone: SequenceStepTone =
    status.state === 'current'
      ? liveTone(status.nodeId)
      : status.state === 'errored'
        ? 'errored'
        : status.state === 'not_taken'
          ? 'notTaken'
          : status.state === 'skipped'
            ? 'skipped'
            : status.state === 'done'
              ? 'done'
              : 'upcoming';
  const isHere = status.state === 'current' || status.state === 'errored';
  // A settled step's badge says how it landed: an invitation still hanging isn't the same "done" as a
  // connection, and a withdrawn one is a third thing again.
  const medallion: SequenceStepMedallion | null =
    tone !== 'done'
      ? null
      : status.outcome === 'invite_pending'
        ? 'pending'
        : status.outcome === 'invite_withdrawn'
          ? 'withdrawn'
          : 'check';
  return {
    tone,
    isLive: status.state === 'current',
    chip: TONE_CHIP[tone],
    chipWide: cn(TONE_CHIP_WIDE[tone] ?? TONE_CHIP[tone], TONE_CHIP[tone] !== '' && TONE_INHERIT),
    ring: TONE_RING[tone],
    isHere,
    stageLabel: isHere && status.nodeId != null ? nodeStatusLabel(status.nodeId) : null,
    tooltip:
      status.state === 'not_taken'
        ? (DEAD_REASON_TOOLTIP[status.deadReason ?? 'branch_not_taken'] ?? TONE_TOOLTIP.notTaken)
        : status.state === 'skipped'
          ? (SKIP_REASON_TOOLTIP[status.skipReason ?? '']?.(ago(status.outcomeAt)) ?? TONE_TOOLTIP.skipped)
          : (connectOutcomeTooltip(status.outcome, status.outcomeAt) ?? TONE_TOOLTIP[tone]),
    medallion,
  };
}

/**
 * Whether a per-contact content override could still change what this contact receives. Only a step that is
 * still ahead of them (or the one they're on) qualifies: a `done` / `skipped` step has already had its
 * moment, and a `not_taken` / `errored` one sits on a lane that will never run — offering an edit there
 * banks a message that can never send, under a toast that says it saved. Unknown status (the builder, or a
 * step the server didn't report) stays editable.
 */
export function isStepContentEditableNow(status: SequenceContactStepStatus | null | undefined): boolean {
  return status == null || status.state === 'upcoming' || status.state === 'current';
}

/** The NOW chip's colours, matched to the live tone so flag and node read as one unit. */
export const NOW_CHIP_CLASS: Record<SequenceStepTone, string> = {
  upcoming: '',
  done: '',
  skipped: '',
  notTaken: '',
  running:
    'bg-blue-50 text-blue-500 ring-blue-200 dark:bg-blue-500/10 dark:text-blue-300 dark:ring-blue-500/40',
  needsYou:
    'bg-blaze-orange-50 text-blaze-orange-600 ring-blaze-orange-200 dark:bg-blaze-orange-500/10 dark:text-blaze-orange-300 dark:ring-blaze-orange-500/40',
  blocked:
    'bg-amber-50 text-amber-700 ring-amber-200 dark:bg-amber-500/10 dark:text-amber-300 dark:ring-amber-500/40',
  errored:
    'bg-fair-pink-50 text-fair-pink-600 ring-fair-pink-200 dark:bg-fair-pink-950/40 dark:text-fair-pink-300 dark:ring-fair-pink-500/40',
};
